import React, { useEffect, useState } from "react";
import api from "../services/api";

type User = {
  username: string;
  email: string;
  role: string;
  created_at?: string;
  whatsapp_number?: string;
  user_id: string;
};

const AdminUsers: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [editUser, setEditUser] = useState<Partial<User> | null>(null);
  const [loading, setLoading] = useState(false);

  // Fetch all users
  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const res = await api.get("/api/admin/users");
      setUsers(res.data);
    } catch (err) {
      alert("Failed to fetch users");
    }
    setLoading(false);
  };

  const handleView = async (user_id: string) => {
    try {
      const res = await api.get(`/api/admin/users/${user_id}`);
      setSelectedUser(res.data);
      setEditUser(null);
    } catch {
      alert("Failed to fetch user details");
    }
  };

  const handleEdit = (user: User) => {
    setEditUser(user);
    setSelectedUser(null);
  };

  const handleEditChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!editUser) return;
    setEditUser({ ...editUser, [e.target.name]: e.target.value });
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editUser || !editUser.user_id) return;
    try {
      await api.put(`/api/admin/users/${editUser.user_id}/update`, editUser);
      setEditUser(null);
      fetchUsers();
    } catch {
      alert("Failed to update user");
    }
  };

  const handleDelete = async (user_id: string) => {
    if (!window.confirm("Are you sure you want to delete this user?")) return;
    try {
      await api.delete(`/api/admin/users/${user_id}`);
      fetchUsers();
    } catch {
      alert("Failed to delete user");
    }
  };

  return (
    <div>
      <h2>Admin User Management</h2>
      {loading ? (
        <p>Loading...</p>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Username</th>
              <th>Email</th>
              <th>Role</th>
              <th>WhatsApp</th>
              <th>Registered</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.user_id}>
                <td>{u.username}</td>
                <td>{u.email}</td>
                <td>{u.role}</td>
                <td>{u.whatsapp_number}</td>
                <td>{u.created_at ? new Date(u.created_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "—"}</td>
                <td>
                  <button onClick={() => handleView(u.user_id)}>View</button>
                  <button onClick={() => handleEdit(u)}>Edit</button>
                  <button onClick={() => handleDelete(u.user_id)}>
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* View User Modal */}
      {selectedUser && (
        <div>
          <h3>User Details</h3>
          <pre>{JSON.stringify(selectedUser, null, 2)}</pre>
          <button onClick={() => setSelectedUser(null)}>Close</button>
        </div>
      )}

      {/* Edit User Modal */}
      {editUser && (
        <form onSubmit={handleEditSubmit}>
          <h3>Edit User</h3>
          <label>
            Email:
            <input
              name="email"
              value={editUser.email || ""}
              onChange={handleEditChange}
            />
          </label>
          <label>
            Role:
            <input
              name="role"
              value={editUser.role || ""}
              onChange={handleEditChange}
            />
          </label>
          <label>
            WhatsApp:
            <input
              name="whatsapp_number"
              value={editUser.whatsapp_number || ""}
              onChange={handleEditChange}
            />
          </label>
          <button type="submit">Save</button>
          <button type="button" onClick={() => setEditUser(null)}>
            Cancel
          </button>
        </form>
      )}
    </div>
  );
};

export default AdminUsers;
